import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import sbeeLogo from "../../assets/sbeelogo.png";
import { ORDER_API, MASTER_API } from "../../apiConfig";

// ── Formats any date string (ISO "2026-07-01", with or without a
//    time component) into dd/mm/yyyy for display. Leaves blank/invalid
//    values untouched.
const formatDate = (value) => {
    if (!value) return "";
    const datePart = String(value).split("T")[0];
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        const [, yyyy, mm, dd] = match;
        return `${dd}/${mm}/${yyyy}`;
    }
    return value;
};

// ── Reads the logged-in user's name from localStorage.
const getLoggedInFirstName = () => {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const data = JSON.parse(raw);
        if (!data.email) return "";

        return data.email.split("@")[0];
    } catch (err) {
        console.error("Failed to read logged-in user:", err);
        return "";
    }
};

const styles = {

    wrapper: {
        background: "#e9e9e9",
        minHeight: "100vh",
        padding: "24px",
        boxSizing: "border-box"
    },

    toolbar: {
        maxWidth: "850px",
        margin: "0 auto 12px auto",
        display: "flex",
        justifyContent: "flex-end"
    },

    printBtn: {
        background: "#1d4ed8",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        padding: "8px 18px",
        fontSize: "14px",
        cursor: "pointer"
    },

    status: {
        maxWidth: "850px",
        margin: "0 auto",
        textAlign: "center",
        padding: "24px",
        fontSize: "14px",
        color: "#444"
    },

    error: {
        color: "#b91c1c"
    },

    slip: {
        background: "#fff",
        maxWidth: "850px",
        margin: "0 auto",
        padding: "28px 32px",
        border: "1px solid #000",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        boxSizing: "border-box"
    },

    header: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        borderBottom: "1px solid #000",
        paddingBottom: "10px"
    },

    logo: {
        height: "48px",
        width: "auto",
        objectFit: "contain"
    },

    titleBlock: {
        textAlign: "center"
    },

    title: {
        fontSize: "18px",
        fontWeight: "bold",
        letterSpacing: "0.5px",
        fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif"
    },

    subtitle: {
        fontSize: "13px",
        fontWeight: "bold",
        marginTop: "4px"
    },

    address: {
        fontSize: "11px",
        fontWeight: "normal",
        marginTop: "4px",
        color: "#333"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed"
    },

    metaTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaTdFirstRow: {
        border: "1px solid #000",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaLabel: {
        fontWeight: "bold",
        width: "18%",
        whiteSpace: "nowrap"
    },

    instruction: {
        fontWeight: "bold",
        fontSize: "13px",
        padding: "8px 4px",
        borderLeft: "1px solid #000",
        borderRight: "1px solid #000"
    },

    th: {
        border: "1px solid #000",
        padding: "6px 4px",
        fontSize: "11px",
        textAlign: "center",
        fontWeight: "bold",
        background: "#f5f5f5",
        overflowWrap: "break-word",
        wordBreak: "break-word"
    },

    td: {
        border: "1px solid #000",
        padding: "6px 4px",
        fontSize: "12px",
        textAlign: "center",
        height: "26px",
        overflowWrap: "break-word",
        wordBreak: "break-word"
    },

    tdLeft: {
        border: "1px solid #000",
        padding: "6px 4px",
        fontSize: "12px",
        textAlign: "left",
        height: "26px",
        overflowWrap: "break-word",
        wordBreak: "break-word"
    },

    colSl: { width: "5%" },
    colProductCode: { width: "11%" },
    colDescription: { width: "22%" },
    colHsn: { width: "9%" },
    colBatchNo: { width: "9%" },
    colDrumNo: { width: "8%" },
    colUom: { width: "6%" },
    colQty: { width: "8%" },

    footerTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerTdFirstRow: {
        border: "1px solid #000",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerLabel: {
        fontWeight: "bold",
        width: "22%"
    }

};

// ── Final Packing List: rendered live from the Outbound Delivery record
//    (GET /outbound-delivery/posted/od/:od_id) every time it's opened.
//    Nothing is generated or stored on the server — "download" is just
//    the browser's Print dialog (Save as PDF), same pattern as the
//    Goods Issue Slip.
export default function OutboundDeliveryform({ odId: odIdProp } = {}) {

    const params = useParams();
    const odId = odIdProp || params.odId;
    const [searchParams] = useSearchParams();
    // Set by the hidden off-screen iframe the Docs tab's Download/Print
    // buttons create — auto-trigger the browser's print/save dialog once the
    // data has loaded, so nothing ever needs a new visible tab.
    const autoPrint = searchParams.get("download") === "true" || searchParams.get("print") === "true";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [unit1Location, setUnit1Location] = useState(null);

    useEffect(() => {

        const fetchData = async () => {
            try {
                setLoading(true);
                setError("");

                const res = await axios.get(
                    `${ORDER_API}/outbound-delivery/posted/od/${odId}?t=${new Date().getTime()}`
                );

                setData(res.data || null);

            } catch (err) {
                console.error(err);
                setError("Failed to load delivery details");
            } finally {
                setLoading(false);
            }
        };

        if (odId) {
            fetchData();
        }

    }, [odId]);

    useEffect(() => {
        const loadUnit1Location = async () => {
            try {
                const res = await axios.get(`${MASTER_API}/location-master`);
                const locations = Array.isArray(res.data) ? res.data : [];
                const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
                setUnit1Location(unit1);
            } catch (err) {
                console.error("LOCATION MASTER FETCH ERROR", err);
                setUnit1Location(null);
            }
        };
        loadUnit1Location();
    }, []);

    useEffect(() => {
        if (autoPrint && !loading && !error && (data?.productDetails || []).length > 0) {
            const timer = setTimeout(() => window.print(), 300);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPrint, loading, error, data]);

    const unit1Name = unit1Location?.company_name || "SBEE CABLES (INDIA) LIMITED";
    const unit1AddressText = unit1Location
        ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
        : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA";

    const rows = data?.productDetails || [];
    const first = rows[0] || {};
    const isEmbedded = !!odIdProp;

    const inner = (
        <>
            {!isEmbedded && !autoPrint && (
                <div style={styles.toolbar} className="odc-no-print">
                    <button style={styles.printBtn} onClick={() => window.print()}>
                        Print / Download
                    </button>
                </div>
            )}

            {loading && (
                <div style={styles.status}>Loading...</div>
            )}

            {error && (
                <div style={{ ...styles.status, ...styles.error }}>
                    {error}
                </div>
            )}

            {!loading && !error && rows.length === 0 && (
                <div style={styles.status}>
                    No delivery found for {odId}
                </div>
            )}

            {!loading && !error && rows.length > 0 && (
                <div style={styles.slip} id="odc-print-area">

                    <div style={styles.header}>
                        <img
                            src={sbeeLogo}
                            alt="SBEE Cables logo"
                            style={styles.logo}
                        />
                        <div style={styles.titleBlock}>
                            <div style={styles.title}>
                                {unit1Name}
                            </div>
                            <div style={styles.address}>
                                {unit1AddressText}
                            </div>
                            <div style={styles.subtitle}>
                                FINAL PACKING LIST
                            </div>
                        </div>
                    </div>

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    Delivery No :
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {data?.od_id || ""}
                                </td>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    Delivery Date :
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {formatDate(first.od_date) || formatDate(new Date())}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Sales Order No :
                                </td>
                                <td style={styles.metaTd}>
                                    {data?.so_id || ""}
                                </td>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Purchase Order No :
                                </td>
                                <td style={styles.metaTd}>
                                    {first.purchase_order || ""}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Customer :
                                </td>
                                <td style={styles.metaTd} colSpan={3}>
                                    {data?.customer || ""}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Shipping Address :
                                </td>
                                <td style={styles.metaTd}>
                                    {data?.shippingAddress || ""}
                                </td>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    Billing Address :
                                </td>
                                <td style={styles.metaTd}>
                                    {data?.billingAddress || ""}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={styles.instruction}>
                    </div>

                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, ...styles.colSl }}>Sl No.</th>
                                <th style={{ ...styles.th, ...styles.colProductCode }}>Product Code</th>
                                <th style={{ ...styles.th, ...styles.colDescription }}>Description</th>
                                <th style={{ ...styles.th, ...styles.colHsn }}>HSN Code</th>
                                <th style={{ ...styles.th, ...styles.colBatchNo }}>Batch No</th>
                                <th style={{ ...styles.th, ...styles.colDrumNo }}>Drum No</th>
                                <th style={{ ...styles.th, ...styles.colUom }}>UOM</th>
                                <th style={{ ...styles.th, ...styles.colQty }}>Delivered Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={r.batch_number ? `${r.batch_number}-${idx}` : idx}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={styles.td}>{r.product_code}</td>
                                    <td style={styles.tdLeft}>{r.description}</td>
                                    <td style={styles.td}>{r.hsn_code || ""}</td>
                                    <td style={styles.td}>{r.batch_number || ""}</td>
                                    <td style={styles.td}>{r.drum_no || ""}</td>
                                    <td style={styles.td}>{r.uom}</td>
                                    <td style={styles.td}>{r.delivery_qty}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.footerTdFirstRow, ...styles.footerLabel }}>
                                    Packed By
                                </td>
                                <td style={styles.footerTdFirstRow}>
                                    {getLoggedInFirstName()}
                                </td>
                                <td style={{ ...styles.footerTdFirstRow, ...styles.footerLabel }}>
                                    Packed Date
                                </td>
                                <td style={styles.footerTdFirstRow}>
                                    {formatDate(first.od_date)}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            )}

            <style>
                {`
                    @media print {
                        .odc-no-print {
                            display: none !important;
                        }
                        body {
                            background: #fff !important;
                        }
                        @page {
                            size: A4;
                            margin: 14mm;
                        }
                    }
                `}
            </style>
        </>
    );

    if (isEmbedded) {
        return (
            <div style={{ padding: "16px", overflowY: "auto", height: "100%" }}>
                {inner}
            </div>
        );
    }

    return (
        <div style={styles.wrapper}>
            {inner}
        </div>
    );

}